import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanMatch, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from 'app/authentication/auth/auth.service';
import { redirectToServerSignIn } from 'app/authentication/auth/auth-redirect.util';
import { IptvClientFlagsService } from 'app/authentication/auth/iptv-client-flags.service';
import { environment } from 'environments/environment';
import { isModuleActive } from 'app/shared/entitlement/module-nav.config';
import { isUiOnlyPhase } from 'app/mocks/mock-api.config';
import { UI_ONLY_PRODUCT_MODULES } from 'app/mocks/ui-only-phase';

@Injectable({ providedIn: 'root' })
export class ModuleGuard implements CanActivate, CanActivateChild, CanMatch {
    constructor(
        private _authService: AuthService,
        private _iptvFlags: IptvClientFlagsService,
        private _router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this._check(route, state.url);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this._check(childRoute, state.url);
    }

    canMatch(route: Route, segments: UrlSegment[]): Observable<boolean> {
        const moduleCode = route.data?.['module'] as string | undefined;
        if (!moduleCode) {
            return of(true);
        }
        return this._authService.check().pipe(
            map(() => this._allowed(moduleCode) || this._iptvFlags.hideLogin),
            catchError(() => of(this._iptvFlags.hideLogin))
        );
    }

    private _check(route: ActivatedRouteSnapshot, url: string): Observable<boolean | UrlTree> {
        const moduleCode = route.data?.['module'] as string | undefined;
        if (!moduleCode) {
            return of(true);
        }
        return this._authService.check().pipe(
            switchMap((authenticated) => {
                if (!authenticated) {
                    if (this._iptvFlags.hideLogin) {
                        return of(this._allowed(moduleCode) ? true : this._router.createUrlTree(['/dashboard']));
                    }
                    redirectToServerSignIn(url);
                    return of(false);
                }
                if (this._authService.isWorkspaceSelectionRequired() && !this._authService.hasWorkspaceSelection()) {
                    return of(this._router.createUrlTree(['/dashboard']));
                }
                return of(this._allowed(moduleCode) ? true : this._router.createUrlTree(['/dashboard']));
            }),
            take(1),
            catchError(() => {
                if (this._iptvFlags.hideLogin) {
                    return of(true);
                }
                redirectToServerSignIn();
                return of(false);
            })
        );
    }

    private _allowed(moduleCode: string): boolean {
        if (environment.user?.superadmin === true) {
            return true;
        }
        if (environment.mockApi?.enabled) {
            const mockModules =
                environment.mockApi.mockModules?.length
                    ? environment.mockApi.mockModules
                    : isUiOnlyPhase()
                      ? UI_ONLY_PRODUCT_MODULES
                      : [];
            if ((mockModules as readonly string[]).includes(moduleCode)) {
                return true;
            }
        }
        const modules = [
            ...((environment.selection?.branch?.modules as string[] | undefined) ?? []),
            ...(Array.isArray(environment.user?.active_modules) ? environment.user.active_modules : []),
            ...(Array.isArray(environment.active_modules) ? environment.active_modules : []),
        ];
        return isModuleActive(moduleCode, modules);
    }
}
